import React, { useState, useEffect } from "react";
import MbLeftBar from "./components/MbLeftBar";
import MbChat from "./components/MbChat";
import MbSubscriptionModal from "./components/MbSubscriptionModal";
import { AiOutlineClose } from "react-icons/ai";
import "./mbbot.css";

import themeClasses from "./themeClasses";
import AuthModal from "./components/modalContent/AuthModal";
import { IoMdLogOut } from "react-icons/io";
import { IoMdLogIn } from "react-icons/io";
import { PiCashRegisterFill } from "react-icons/pi";

export default function App() {
  const [isHydrated, setIsHydrated] = useState(false);

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  const [isLoggedIn, setIsLoggedIn] = useState(
    !!localStorage.getItem("access_token")
  );

  // ✅ NEW: user state
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem("mb_user");
    return savedUser ? JSON.parse(savedUser) : null;
  });

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    setIsLoggedIn(!!token);
  }, []);

  // ============================
  // MAIN APP STATES
  // ============================
  const [chats, setChats] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    if (!user) {
      setChats([]);
      setProjects([]);
      setIsHydrated(true);
      return;
    }

    const storedChats = localStorage.getItem(`mbbot-chats-${user.id}`);
    const storedProjects = localStorage.getItem(
      `mb_project_chat_data-${user.id}`
    );

    setChats(storedChats ? JSON.parse(storedChats) : []);
    setProjects(storedProjects ? JSON.parse(storedProjects) : []);
    setIsHydrated(true);
  }, [user]);

  useEffect(() => {
    if (!isLoggedIn && isHydrated && chats.length === 0) {
      const guestChat = {
        id: "guest-chat",
        name: "New Chat",
        messages: [],
      };

      setChats([guestChat]);
      setActiveChatId("guest-chat");
    }
  }, [isLoggedIn, isHydrated, chats.length]);

  const [activeChatId, setActiveChatId] = useState(() => {
    const saved = localStorage.getItem("mbbot-activeChatId");
    return saved ? JSON.parse(saved) : null;
  });

  const [currentTheme, setCurrentTheme] = useState(
    () => localStorage.getItem("mbbot-theme") || "light"
  );

  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeProjectChatId, setActiveProjectChatId] = useState(null);

  // =====================
  // Effects
  // =====================
  useEffect(() => {
    document.body.setAttribute("data-theme", currentTheme);
    localStorage.setItem("mbbot-theme", currentTheme);
  }, [currentTheme]);

  useEffect(() => {
    if (!user || !isHydrated) return;

    localStorage.setItem(`mbbot-chats-${user.id}`, JSON.stringify(chats));
  }, [chats, user, isHydrated]);

  useEffect(() => {
    if (!user || !isHydrated) return;

    localStorage.setItem(
      `mb_project_chat_data-${user.id}`,
      JSON.stringify(projects)
    );
  }, [projects, user, isHydrated]);

  useEffect(() => {
    if (!isHydrated) return;

    if (!activeChatId && chats.length > 0) {
      setActiveChatId(chats[0].id);
    }
  }, [isHydrated, chats, activeChatId]);

  useEffect(() => {
    if (activeChatId !== null)
      localStorage.setItem("mbbot-activeChatId", JSON.stringify(activeChatId));
  }, [activeChatId]);

  useEffect(() => {
    if (chats.length > 0 && !activeChatId) {
      setActiveChatId(chats[0].id);
    }
  }, [chats, activeChatId]);

  useEffect(() => {
    const handleLogout = () => {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      setIsLoggedIn(false);
      setUser(null); // clear user
      setActiveChatId(null);
      setActiveProjectChatId(null);
      setChats([]);
      setProjects([]);
    };

    window.addEventListener("auth-logout", handleLogout);
    return () => window.removeEventListener("auth-logout", handleLogout);
  }, []);

  // =====================
  // Chat Handlers
  // =====================
  const handleUpdateChatMessages = (updatedMessages, chatId) => {
    setChats((prev) =>
      prev.map((chat) => {
        const firstUserMessage = updatedMessages.find(
          (m) => m.sender === "user"
        );
        const autoName =
          firstUserMessage?.text?.trim().slice(0, 40) || chat.name;
        return chat.id === chatId
          ? {
              ...chat,
              messages: updatedMessages,
              name: chat.name === "New Chat" ? autoName : chat.name,
            }
          : chat;
      })
    );
  };

  const handleSelectChat = (chatId, isProjectChat = false) => {
    if (isProjectChat) {
      setActiveProjectChatId(chatId);
      setActiveChatId(null);
    } else {
      setActiveChatId(chatId);
      setActiveProjectChatId(null);
    }
  };

  const handleNewChat = () => {
    const newId = Date.now();
    const newChat = { id: newId, name: "New Chat", messages: [] };
    setChats((prev) => [...prev, newChat]);
    setActiveChatId(newId);
    setActiveProjectChatId(null);
    localStorage.setItem(`mbChatHistory_${newId}`, JSON.stringify([]));
  };

  const handleDeleteChat = (chatId) => {
    setChats((prev) => {
      const remaining = prev.filter((c) => c.id !== chatId);
      if (remaining.length === 0) {
        const newId = Date.now();
        const newChat = { id: newId, name: "New Chat", messages: [] };
        localStorage.setItem(`mbChatHistory_${newId}`, JSON.stringify([]));
        setActiveChatId(newId);
        return [newChat];
      }
      if (activeChatId === chatId) setActiveChatId(remaining[0].id);
      return remaining;
    });
    localStorage.removeItem(`mbChatHistory_${chatId}`);
  };

  const handleEditChat = (chatId, newName) => {
    setChats((prev) =>
      prev.map((c) => (c.id === chatId ? { ...c, name: newName } : c))
    );
  };

  // =====================
  // Project Handlers
  // =====================
  const handleUpdateProjectChatMessages = (updatedMessages, projectChatId) => {
    setProjects((prev) =>
      prev.map((proj) =>
        proj.chat.id === projectChatId
          ? {
              ...proj,
              chat: {
                ...proj.chat,
                messages: updatedMessages,
                name:
                  updatedMessages
                    .find((m) => m.sender === "user")
                    ?.text?.trim()
                    .slice(0, 40) || proj.chat.name,
              },
            }
          : {
              ...proj,
              subprojects: proj.subprojects?.map((sub) =>
                sub.chat.id === projectChatId
                  ? {
                      ...sub,
                      chat: {
                        ...sub.chat,
                        messages: updatedMessages,
                        name:
                          updatedMessages
                            .find((m) => m.sender === "user")
                            ?.text?.trim()
                            .slice(0, 40) || sub.chat.name,
                      },
                    }
                  : sub
              ),
            }
      )
    );
  };

  const handleAddProject = (projectName) => {
    if (!projectName) return;
    const timestamp = Date.now();
    const newChatId = `chat-${timestamp}`;
    const newProj = {
      id: `proj-${timestamp}`,
      name: projectName,
      chat: { id: newChatId, name: "New Chat", messages: [] },
      subprojects: [],
    };
    setProjects((prev) => [...prev, newProj]);
    setActiveProjectChatId(newChatId);
    setActiveChatId(null);
    localStorage.setItem(`mbChatHistory_${newChatId}`, JSON.stringify([]));
  };

  const handleAddSubproject = (projectId, subName) => {
    if (!subName) return;
    const timestamp = Date.now();
    const newSubChatId = `chat-${timestamp}`;
    const newSub = {
      id: `sub-${timestamp}`,
      name: subName,
      chat: { id: newSubChatId, name: "New Chat", messages: [] },
    };
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? { ...p, subprojects: [...(p.subprojects || []), newSub] }
          : p
      )
    );
    setActiveProjectChatId(newSubChatId);
    setActiveChatId(null);
    localStorage.setItem(`mbChatHistory_${newSubChatId}`, JSON.stringify([]));
  };

  const handleDeleteProject = (projectId) => {
    setProjects((prev) => prev.filter((p) => p.id !== projectId));
    if (activeProjectChatId) {
      const exists = projects.find((p) => p.chat.id === activeProjectChatId);
      if (exists?.id === projectId) setActiveProjectChatId(null);
    }
  };

  const handleDeleteSubproject = (projectId, subId) => {
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? { ...p, subprojects: p.subprojects.filter((s) => s.id !== subId) }
          : p
      )
    );
    const subChatKey = projects
      .find((p) => p.id === projectId)
      ?.subprojects.find((s) => s.id === subId)?.chat.id;
    if (subChatKey) {
      localStorage.removeItem(`mbChatHistory_${subChatKey}`);
      if (activeProjectChatId === subChatKey) setActiveProjectChatId(null);
    }
  };

  const handleEditProject = (projectId, newName) => {
    if (!newName) return;
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? {
              ...p,
              name: newName,
              chat: { ...p.chat, name: `${newName} Chat` },
            }
          : p
      )
    );
  };

  const handleEditSubproject = (projectId, subId, newName) => {
    if (!newName) return;
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? {
              ...p,
              subprojects: p.subprojects.map((s) =>
                s.id === subId
                  ? {
                      ...s,
                      name: newName,
                      chat: { ...s.chat, name: `${newName} Chat` },
                    }
                  : s
              ),
            }
          : p
      )
    );
  };

  // =====================
  // Misc Handlers
  // =====================
  const handleToggleSubscription = () =>
    setShowSubscriptionModal(!showSubscriptionModal);
  const handleChangeTheme = (theme) => setCurrentTheme(theme);
  const handleSidebarToggle = (collapsed) => setSidebarCollapsed(collapsed);

  const activeChat = chats.find((c) => c.id === activeChatId);
  const activeProjectChatObj = (() => {
    for (const proj of projects) {
      if (proj.chat.id === activeProjectChatId) return proj.chat;
      const sub = proj.subprojects?.find(
        (s) => s.chat.id === activeProjectChatId
      );
      if (sub) return sub.chat;
    }
    return null;
  })();

  // =====================
  // AUTH HANDLERS
  // =====================
  // const handleLoginSuccess = (loggedInUser) => {
  //   localStorage.setItem("mb_user", JSON.stringify(loggedInUser));
  //   setUser(loggedInUser); // set user state
  //   setIsLoggedIn(true);
  //   setShowAuthModal(false);

  //   // ✅ create a new chat immediately if user has no chats
  //   const userChats = localStorage.getItem(`mbbot-chats-${loggedInUser.id}`);
  //   if (!userChats || JSON.parse(userChats).length === 0) {
  //     handleNewChat(); // create first chat for this user
  //   } else {
  //     // set first chat as active
  //     const firstChat = JSON.parse(userChats)[0];
  //     setActiveChatId(firstChat.id);
  //   }
  // };

  const handleLoginSuccess = (loggedInUser) => {
    localStorage.setItem("mb_user", JSON.stringify(loggedInUser));
    setUser(loggedInUser);
    setIsLoggedIn(true);
    setShowAuthModal(false);

    const chatKey = `mbbot-chats-${loggedInUser.id}`;
    const storedChats = localStorage.getItem(chatKey);
    const parsedChats = storedChats ? JSON.parse(storedChats) : [];

    // ✅ create ONLY if user has never had chats
    if (parsedChats.length === 0) {
      const newId = Date.now();
      const newChat = { id: newId, name: "New Chat", messages: [] };

      localStorage.setItem(chatKey, JSON.stringify([newChat]));
      setChats([newChat]);
      setActiveChatId(newId);
    } else {
      setChats(parsedChats);
      setActiveChatId(parsedChats[0].id);
    }
  };

  // =====================
  // RENDER
  // =====================
  return (
    <div className={`${themeClasses[currentTheme].body} App min-h-screen`}>
      <div className="mb-main-wrapper w-full max-w-full h-screen flex">
        {/* Top Right Login/Register/Logout Buttons */}
        <div className="absolute top-3 right-5 z-[999] flex gap-3">
          {!isLoggedIn && (
            <>
              <button
                className="p-2 bg-[#4caf50] text-white rounded-md flex gap-1 items-center"
                onClick={() => {
                  setAuthMode("login");
                  setShowAuthModal(true);
                }}
              >
                <IoMdLogIn size={20} /> Login
              </button>
              <button
                className="p-2 bg-green-600 text-white rounded-md flex gap-1 items-center"
                onClick={() => {
                  setAuthMode("register");
                  setShowAuthModal(true);
                }}
              >
                <PiCashRegisterFill size={20} /> Register
              </button>
            </>
          )}
          {isLoggedIn && (
            <button
              className="p-2 bg-red-600 text-white rounded-md flex gap-1 items-center"
              onClick={() => {
                localStorage.removeItem("access_token");
                localStorage.removeItem("refresh_token");
                localStorage.removeItem("mb_user");
                setChats([]);
                setProjects([]);
                setUser(null);
                setIsLoggedIn(false);
              }}
            >
              <IoMdLogOut size={20} /> Logout
            </button>
          )}
        </div>

        {/* LEFT BAR */}
        {isLoggedIn && (
          <MbLeftBar
            chats={chats}
            activeChatId={activeChatId}
            onSelectChat={handleSelectChat}
            onNewChat={handleNewChat}
            onDeleteChat={handleDeleteChat}
            onEditChat={handleEditChat}
            onToggleSubscription={handleToggleSubscription}
            onChangeTheme={handleChangeTheme}
            currentTheme={currentTheme}
            projects={projects}
            onAddProject={handleAddProject}
            onDeleteProject={handleDeleteProject}
            onEditProject={handleEditProject}
            onAddSubproject={handleAddSubproject}
            onDeleteSubproject={handleDeleteSubproject}
            onEditSubproject={handleEditSubproject}
            onSidebarToggle={handleSidebarToggle}
          />
        )}

        {/* CHAT AREA */}
        <div
          className={`${themeClasses[currentTheme].chatbotWrapper}
    mb-chatbot-wrapper p-5 pb-20 pr-1 absolute top-0 right-0 bottom-0 transition-all duration-300 ${
      !isLoggedIn ? "left-0" : sidebarCollapsed ? "left-[60px]" : "left-[200px]"
    }`}
        >
          {/* ✅ PROJECT / SUB-PROJECT CHAT */}
          {activeProjectChatId && activeProjectChatObj ? (
            <MbChat
              key={activeProjectChatId}
              chatId={activeProjectChatId}
              initialContent={activeProjectChatObj.messages || []}
              onUpdateContent={(msgs) =>
                handleUpdateProjectChatMessages(msgs, activeProjectChatId)
              }
              currentTheme={currentTheme}
              isLoggedIn={isLoggedIn}
              showAuthModal={showAuthModal}
              setShowAuthModal={setShowAuthModal}
            />
          ) : (
            /* ✅ NORMAL / GUEST CHAT */
            <MbChat
              key={activeChatId || "guest"}
              chatId={activeChatId || "guest"}
              initialContent={activeChat?.messages || []}
              onUpdateContent={(msgs) => {
                if (isLoggedIn && activeChatId) {
                  handleUpdateChatMessages(msgs, activeChatId);
                } else {
                  setChats((prev) =>
                    prev.map((c) =>
                      c.id === "guest-chat" ? { ...c, messages: msgs } : c
                    )
                  );
                }
              }}
              onNewChat={handleNewChat}
              currentTheme={currentTheme}
              isLoggedIn={isLoggedIn}
              showAuthModal={showAuthModal}
              setShowAuthModal={setShowAuthModal}
            />
          )}
        </div>
      </div>

      {/* SUBSCRIPTION MODAL */}
      {showSubscriptionModal && (
        <div
          className="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={handleToggleSubscription}
        >
          <div
            className={`${themeClasses[currentTheme].modal} ${themeClasses[currentTheme].pricingModal} pricing-modal absolute flex items-center inset-[10px] p-5 rounded-xl shadow-lg overflow-y-auto`}
            onClick={(e) => e.stopPropagation()}
          >
            <MbSubscriptionModal currentTheme={currentTheme} />
            <button
              className="modalClose absolute top-5 right-5 bg-none border-0"
              onClick={handleToggleSubscription}
            >
              <AiOutlineClose
                className={` text-xl ${
                  currentTheme === "dark" ? "text-white" : "text-black"
                }`}
              />
            </button>
          </div>
        </div>
      )}

      {/* AUTH MODAL */}
      {showAuthModal && (
        <AuthModal
          mode={authMode}
          setMode={setAuthMode}
          onLoginSuccess={handleLoginSuccess}
          currentTheme={currentTheme}
          onClose={() => setShowAuthModal(false)}
        />
      )}
    </div>
  );
}
